import { type ReactNode, useCallback, useMemo } from 'react';
import type { GraphQLError } from 'graphql';
import { has, noop } from 'lodash';
import type { CopyCommentsModeInput } from '@frameio/graphql-client/generated';
import { useTrackUnsafe } from '@frameio/shared/segment/hooks';
import { startEvent } from '@frameio/telemetry';
import { CoreAssetsEventNames } from '@frameio/telemetry/events/analytics/core-assets';
import { BULK_OPERATION_THRESHOLD } from '../../../bulk-operations/constants';
import type { AssetParentFragment } from '../../../shared/gql/generated/AssetParent';
import { useAssetOperationCheck } from '../../../shared/hooks/useAssetOperationCheck';
import {
  CopyCommentsFilterOptions,
  CopyCommentsOptions,
} from '../../constants';
import { checkCanPerformBulkAction } from '../../utils';
import {
  type AssetOperationArgs,
  useAssetWarningFlow,
} from '../useAssetWarningFlow';
import { useBulkActionLimitToast } from '../useBulkActionLimitToast';
import TEXT from './generated/TEXT/useCopyAssets';
import { useBulkCopyAssets } from './useBulkCopyAssets';
import { useSyncCopyAssets } from './useSyncCopyAssets';

type CopyAssetsArgs = AssetOperationArgs & {
  /** the array of ids of the assets to copy */
  assetIds: string[];
  /** how should comments be copied? */
  copyComments?: CopyCommentsModeInput;
  /** the destination folder asset (requires id, name and projectId) */
  destination: AssetParentFragment;
  /**
   * is the copy operation a duplicate? this will add an imcrementing number "(1)"
   * to the new asset and display a different toast message
   */
  duplicate?: boolean;
  /** whether to force the refetch of the finder */
  forceRefetch?: boolean;
  /** whether to force the move, skipping any checks */
  force?: boolean;
  /** an optional callback fired if the copy fails */
  onError?: () => void;
  /** an optional callback fired if the copy succeeds */
  onSuccess?: () => void;
};

export type CopyAssetsReturn = {
  copyAssets: (args: CopyAssetsArgs) => void;
  copyRestrictedFolderDialog: ReactNode;
  copyCrossProjectDialog: ReactNode;
};

/**
 * The useCopyAssets hook can be used to copy a set of assets given an array of
 * asset ids. It performs any necessary cache updates to add the newly copied
 * assets to the cache.
 *
 * @param opts
 * @returns a function to call when ready to copy the assets
 */
export function useCopyAssets(): CopyAssetsReturn {
  const track = useTrackUnsafe();
  const addBulkActionLimitToast = useBulkActionLimitToast();
  const copyAssets = useSyncCopyAssets();
  const bulkCopyAssets = useBulkCopyAssets();
  const { checkMoveCopyPreconditions } = useAssetOperationCheck();

  // Function to execute the copy operation without warnings
  const executeDirectCopy = useCallback(
    async ({
      assetIds,
      copyComments = CopyCommentsOptions.NONE,
      destination,
      duplicate = false,
      onError = noop,
      onSuccess = noop,
      forceRefetch = false,
    }: CopyAssetsArgs) => {
      const isBulkAssetOperation = assetIds.length >= BULK_OPERATION_THRESHOLD;

      track(
        'assets-copied',
        {
          assets_count: assetIds.length,
          is_bulk_operation: isBulkAssetOperation,
          ...(copyComments &&
            has(CopyCommentsFilterOptions, copyComments) && {
              comment_option:
                CopyCommentsFilterOptions[
                  copyComments as keyof typeof CopyCommentsFilterOptions
                ],
            }),
        },
        { logToDatadog: true }
      );

      const action = duplicate ? 'duplicate' : 'copy';

      if (
        !checkCanPerformBulkAction({
          assetCount: assetIds.length,
          action,
        })
      ) {
        addBulkActionLimitToast(action);
        return;
      }

      if (isBulkAssetOperation) {
        await bulkCopyAssets({
          assetIds,
          destination,
          copyComments,
          duplicate,
        });
        return;
      }

      const copyEvent = startEvent(CoreAssetsEventNames.CopyAssets, {
        asset_count: assetIds.length,
        asset_ids: assetIds,
        destination_id: destination.id,
      });

      const handleError = (error?: readonly GraphQLError[]) => {
        onError();
        copyEvent.failed({ error });
      };

      const handleSuccess = () => {
        onSuccess();
        copyEvent.completed();
      };

      await copyAssets({
        assetIds,
        destination,
        duplicate,
        copyComments,
        onError: handleError,
        onSuccess: handleSuccess,
        forceRefetch,
      });
    },
    [addBulkActionLimitToast, bulkCopyAssets, copyAssets, track]
  );

  const {
    warningState,
    startWarningFlow,
    createWarningDialog,
    operationArgs: copyAssetsArgs,
  } = useAssetWarningFlow<CopyAssetsArgs>({
    executeOperation: executeDirectCopy,
    cancelText: TEXT.cancel,
  });

  // Main copy assets handler that uses the consolidated check
  const copyAssetsCallback = useCallback(
    async (args: CopyAssetsArgs) => {
      const { assetIds, force = false, destination } = args;

      if (force) {
        // Skip warnings, execute the operation directly
        executeDirectCopy(args);
      } else {
        const checkResult = await checkMoveCopyPreconditions({
          assetIds,
          destination,
        });

        if (checkResult.shouldShowWarning) {
          startWarningFlow({
            ...args,
            triggeredByRestricted: checkResult.isAnyRestricted,
            triggeredByCrossProject: checkResult.isCrossProject,
          });
        } else {
          executeDirectCopy(args);
        }
      }
    },
    [executeDirectCopy, checkMoveCopyPreconditions, startWarningFlow]
  );

  // Use the createWarningDialog factory for both dialogs
  const duplicate = copyAssetsArgs.current?.duplicate === true;
  const copyRestrictedFolderTitle = duplicate
    ? TEXT.duplicateTitle
    : TEXT.copyTitle;
  const copyRestrictedFolderDetails = duplicate
    ? TEXT.duplicateDetails
    : TEXT.copyDetails;
  const copyRestrictedFolderConfirm = duplicate
    ? TEXT.duplicateFolder
    : TEXT.copyFolder;
  const copyRestrictedFolderDialog = useMemo(() => {
    return createWarningDialog({
      isShowing: warningState === 'restricted',
      title: copyRestrictedFolderTitle,
      details: copyRestrictedFolderDetails,
      confirmButtonText: copyRestrictedFolderConfirm,
    });
  }, [
    createWarningDialog,
    warningState,
    copyRestrictedFolderTitle,
    copyRestrictedFolderDetails,
    copyRestrictedFolderConfirm,
  ]);

  const copyCrossProjectWarningConfirm = duplicate
    ? TEXT.duplicateAnyway
    : TEXT.copyAnyway;
  const copyCrossProjectWarningTitle = TEXT.metadataWarningTitle;
  const copyCrossProjectWarningDescription = TEXT.metadataWarningDescription;
  const copyCrossProjectDialog = useMemo(() => {
    return createWarningDialog({
      isShowing: warningState === 'cross-project',
      title: copyCrossProjectWarningTitle,
      details: copyCrossProjectWarningDescription,
      confirmButtonText: copyCrossProjectWarningConfirm,
    });
  }, [
    createWarningDialog,
    warningState,
    copyCrossProjectWarningConfirm,
    copyCrossProjectWarningTitle,
    copyCrossProjectWarningDescription,
  ]);

  return {
    copyAssets: copyAssetsCallback,
    copyRestrictedFolderDialog,
    copyCrossProjectDialog,
  };
}

TEXT.DEFINE({
  cancel: 'Cancel',
  copyFolder: 'Copy',
  copyDetails:
    'Folder members will not be added to the copy. Only you will have access.',
  copyTitle: "You're about to copy a restricted folder",
  duplicateFolder: 'Duplicate',
  duplicateDetails:
    'Folder members will not be added to the duplicate. Only you will have access.',
  duplicateTitle: "You're about to duplicate a restricted folder",
  metadataWarningTitle: 'Custom fields will be lost',
  metadataWarningDescription:
    'Any custom fields or field values will be lost on copied assets in a new project.',
  copyAnyway: 'Copy Anyway',
  duplicateAnyway: 'Duplicate Anyway',
});
